// frontend entry point for app specific code
import React, { lazy } from 'react';
const AppHome = lazy(() => import('./pages/AppHome').then(m => ({ default: m.AppHome })));
const AppSampleHello = lazy(() => import('./pages/AppSampleHello'));
const CreateSite = lazy(() => import('./pages/CreateSite'));
const CreateSiteAI = lazy(() => import('./pages/CreateSiteAI'));
const CreateSiteFromUrl = lazy(() => import('./pages/CreateSiteFromUrl'));
const CreateSiteAIComplete = lazy(() => import('./pages/CreateSiteAIComplete'));
const SiteBuilding = lazy(() => import('./pages/SiteBuilding'));
const SitePreview = lazy(() => import('./pages/SitePreview'));
const SiteEditor = lazy(() => import('./pages/SiteEditor'));
const SiteBuilderPanel = lazy(() => import('./pages/SiteBuilderPanel'));
const SharePage = lazy(() => import('./pages/SharePage'));
const RestoreSitePage = lazy(() => import('./pages/RestoreSitePage'));
const FormTypesPage = lazy(() => import('./pages/FormTypesPage'));

// Lazy-load admin widgets (only loaded when needed)
const SystemHealthWidget = lazy(() => import('./admin/widgets/SystemHealthWidget'));
const BusinessMetricsWidget = lazy(() => import('./admin/widgets/BusinessMetricsWidget'));
const AIHealthWidget = lazy(() => import('./admin/widgets/AIHealthWidget'));
const AICreditUsageWidget = lazy(() => import('./admin/widgets/AICreditUsageWidget'));

// Export getAppRoutes for custom app routes
// Note: It is recommended to prefix custom routes with /app to avoid collisions
// with core system routes like /dashboard, /manage, etc.
export function getAppRoutes({ Route, LazyRoute, ProtectedRoute }) {
  return [
    <Route
      key="app-home"
      path="/app/home"
      element={<LazyRoute element={<AppHome />} />}
    />,
    <Route
      key="app-hello"
      path="/app/hello"
      element={<LazyRoute element={<AppSampleHello />} />}
    />,
    <Route
      key="app-create-site"
      path="/app/create-site"
      element={<LazyRoute element={<CreateSite />} />}
    />,
    <Route
      key="app-create-site-ai"
      path="/app/create-site-ai"
      element={<LazyRoute element={<CreateSiteAI />} />}
    />,
    <Route
      key="app-create-site-from-url"
      path="/app/create-site-from-url"
      element={<LazyRoute element={<CreateSiteFromUrl />} />}
    />,
    <Route
      key="app-create-site-ai-complete"
      path="/app/create-site-ai-complete"
      element={<LazyRoute element={<CreateSiteAIComplete />} />}
    />,
    <Route
      key="app-site-building"
      path="/app/site-building/:jobId"
      element={<LazyRoute element={<SiteBuilding />} />}
    />,
    <Route
      key="app-site-preview"
      path="/app/site-preview/:siteId"
      element={<LazyRoute element={<SitePreview />} />}
    />,
    <Route
      key="app-site-editor"
      path="/app/site-editor/:siteId"
      element={<LazyRoute element={<SiteEditor />} />}
    />,
    <Route
      key="app-site-builder"
      path="/app/site-builder/:siteId"
      element={<LazyRoute element={<SiteBuilderPanel />} />}
    />,
    <Route
      key="app-share"
      path="/app/share"
      element={<LazyRoute element={<SharePage />} />}
    />,
    <Route
      key="app-restore-site"
      path="/app/restore-site"
      element={<LazyRoute element={<RestoreSitePage />} />}
    />,
    <Route
      key="app-form-types"
      path="/app/site-builder/:siteId/form-types"
      element={<LazyRoute element={<FormTypesPage />} />}
    />,
  ];
}

/**
 * Export admin widgets for core AdminPage to load
 * This maintains loose coupling - core never directly imports from app/
 * 
 * Pattern matches getAppRoutes() - exported function that core can call
 * 
 * @returns {Array} Array of widget configurations
 */
export function getAdminWidgets() {
  return [
    {
      id: 'system-health',
      title: 'System Health',
      description: 'Active jobs and system status',
      component: SystemHealthWidget,
      refreshInterval: 30000, // ms - auto-refresh every 30 seconds
      order: 1
    },
    {
      id: 'business-metrics',
      title: 'Business Metrics',
      description: 'Users, sites, and credits overview',
      component: BusinessMetricsWidget,
      refreshInterval: 60000, // ms - auto-refresh every 60 seconds
      order: 2
    },
    {
      id: 'ai-health',
      title: 'AI Health',
      description: 'AI generation performance and errors',
      component: AIHealthWidget,
      refreshInterval: 30000, // ms - auto-refresh every 30 seconds
      order: 3
    },
    {
      id: 'ai-credit-usage',
      title: 'AI Credit Usage',
      description: 'Credit usage by provider (OpenAI vs OpenRouter)',
      component: AICreditUsageWidget,
      refreshInterval: 300000, // ms - auto-refresh every 5 minutes
      order: 4
    }
  ];
}